import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChild, ElementRef, NgZone, ChangeDetectorRef } from "@angular/core";
import { SpreadsheetProps, SpreadsheetData } from "../../interfaces/builder-screen.models";
import { NzMessageService } from "ng-zorro-antd/message";
import { NzUploadFile } from "ng-zorro-antd/upload";

// Implementação customizada - não usar biblioteca externa problemática

interface CellData {
  value: string;
  formula?: string;
  style?: any;
}

@Component({
  selector: "app-builder-spreadsheet",
  templateUrl: "./spreadsheet.component.html",
  styleUrls: ["./spreadsheet.component.scss"],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class BuilderSpreadsheetComponent implements OnInit, OnDestroy {
  @Input() props!: SpreadsheetProps;
  @Output() dataChange = new EventEmitter<SpreadsheetData>();

  // Dados da planilha customizada
  public spreadsheetData: CellData[][] = [];
  public isInitialized = false;
  public currentFormula = "";
  public selectedCell = { row: -1, col: -1 };
  public editingCell = { row: -1, col: -1 };
  
  // Configurações da planilha
  public rows = 20;
  public cols = 10;
  public columnHeaders: string[] = [];
  public rowNumbers: number[] = [];

  constructor(
    private message: NzMessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    console.log('=== INICIALIZANDO PLANILHA CUSTOMIZADA ===');
    
    // Inicializar props padrão
    this.props = {
      showToolbar: true,
      showFormulaBar: true,
      readonly: false,
      maxRows: 20,
      maxColumns: 10,
      defaultRowHeight: 25,
      defaultColumnWidth: 100,
      backgroundColor: "#ffffff",
      borderColor: "#d9d9d9",
      headerBackgroundColor: "#f5f5f5",
      headerTextColor: "#262626",
      gridColor: "#e8e8e8",
      ...this.props
    };

    // Configurar dimensões da planilha
    this.rows = this.props.maxRows || 20;
    this.cols = this.props.maxColumns || 10;

    // Inicializar planilha customizada
    this.initializeCustomSpreadsheet();
  }

  private initializeCustomSpreadsheet(): void {
    console.log('Inicializando planilha customizada...');
    
    try {
      // Gerar cabeçalhos das colunas (A, B, C, ...)
      this.columnHeaders = [];
      for (let i = 0; i < this.cols; i++) {
        this.columnHeaders.push(String.fromCharCode(65 + i));
      }

      // Gerar números das linhas (1, 2, 3, ...)
      this.rowNumbers = [];
      for (let i = 0; i < this.rows; i++) {
        this.rowNumbers.push(i + 1);
      }

      // Inicializar dados da planilha
      this.spreadsheetData = [];
      for (let row = 0; row < this.rows; row++) {
        this.spreadsheetData[row] = [];
        for (let col = 0; col < this.cols; col++) {
          this.spreadsheetData[row][col] = { value: '' };
        }
      }

      // Carregar dados padrão
      this.loadDefaultData();

      this.isInitialized = true;
      console.log('✅ Planilha customizada inicializada com sucesso!');
      this.message.success('Planilha carregada com sucesso!');

    } catch (error) {
      console.error('Erro ao inicializar planilha customizada:', error);
      this.message.error('Erro ao carregar planilha');
    }
  }

  ngOnDestroy() {
    // Limpeza simples - sem bibliotecas externas problemáticas
    console.log('Limpando componente de planilha customizada');
  }

  private loadDefaultData(): void {
    // Dados padrão para demonstração
    if (this.spreadsheetData.length > 0) {
      this.spreadsheetData[0][0] = { value: 'Nome' };
      this.spreadsheetData[0][1] = { value: 'Idade' };
      this.spreadsheetData[0][2] = { value: 'Email' };
      
      this.spreadsheetData[1][0] = { value: 'João Silva' };
      this.spreadsheetData[1][1] = { value: '30' };
      this.spreadsheetData[1][2] = { value: 'joao@email.com' };
      
      this.spreadsheetData[2][0] = { value: 'Maria Santos' };
      this.spreadsheetData[2][1] = { value: '25' };
      this.spreadsheetData[2][2] = { value: 'maria@email.com' };
    }
  }

  // Métodos para interação com a planilha
  public selectCell(row: number, col: number): void {
    if (!this.props.readonly) {
      this.selectedCell = { row, col };
      console.log('Célula selecionada:', row, col);
    }
  }

  public startEditing(row: number, col: number): void {
    if (!this.props.readonly) {
      this.editingCell = { row, col };
      this.selectedCell = { row, col };
      console.log('Iniciando edição:', row, col);
    }
  }

  public stopEditing(): void {
    if (this.editingCell.row >= 0 && this.editingCell.col >= 0) {
      console.log('Parando edição:', this.editingCell.row, this.editingCell.col);
      this.editingCell = { row: -1, col: -1 };
      this.emitDataChange();
    }
  }

  public onCellChange(row: number, col: number, value: string): void {
    if (this.spreadsheetData[row] && this.spreadsheetData[row][col]) {
      this.spreadsheetData[row][col].value = value;
      console.log('Célula alterada:', row, col, value);
      this.emitDataChange();
    }
  }

  public onKeyDown(event: KeyboardEvent, row: number, col: number): void {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        this.moveToNextRow(row, col);
        break;
      case 'Tab':
        event.preventDefault();
        this.moveToNextColumn(row, col);
        break;
      case 'Escape':
        this.stopEditing();
        break;
      case 'ArrowUp':
        if (!this.isEditing(row, col)) {
          event.preventDefault();
          this.selectCell(Math.max(0, row - 1), col);
        }
        break;
      case 'ArrowDown':
        if (!this.isEditing(row, col)) {
          event.preventDefault();
          this.selectCell(Math.min(this.rows - 1, row + 1), col);
        }
        break;
      case 'ArrowLeft':
        if (!this.isEditing(row, col)) {
          event.preventDefault();
          this.selectCell(row, Math.max(0, col - 1));
        }
        break;
      case 'ArrowRight':
        if (!this.isEditing(row, col)) {
          event.preventDefault();
          this.selectCell(row, Math.min(this.cols - 1, col + 1));
        }
        break;
    }
  }

  private moveToNextRow(row: number, col: number): void {
    const nextRow = row + 1;
    if (nextRow < this.rows) {
      this.selectCell(nextRow, col);
      this.focusCell(nextRow, col);
    }
  }

  private moveToNextColumn(row: number, col: number): void {
    const nextCol = col + 1;
    if (nextCol < this.cols) {
      this.selectCell(row, nextCol);
      this.focusCell(row, nextCol);
    } else if (row + 1 < this.rows) {
      this.selectCell(row + 1, 0);
      this.focusCell(row + 1, 0);
    }
  }

  private focusCell(row: number, col: number): void {
    setTimeout(() => {
      const input = document.getElementById(`cell-${row}-${col}`) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 10);
  }

  public isSelected(row: number, col: number): boolean {
    return this.selectedCell.row === row && this.selectedCell.col === col;
  }

  public isEditing(row: number, col: number): boolean {
    return this.editingCell.row === row && this.editingCell.col === col;
  }

  private emitDataChange(): void {
    // Emitir mudanças para o builder
    const data: SpreadsheetData = {
      sheets: [{
        name: 'Planilha1',
        data: this.spreadsheetData,
        formulas: {},
        styles: {},
        merges: [],
        rowHeights: {},
        columnWidths: {}
      }],
      activeSheetIndex: 0
    };
    
    this.dataChange.emit(data);
  }

  // Métodos públicos para a toolbar
  public addSheet(): void {
    this.message.info('Funcionalidade de adicionar aba será implementada em versão futura');
  }

  public exportExcel(): void {
    this.message.info('Funcionalidade de exportar será implementada em versão futura');
  }

  public importExcel(file: NzUploadFile): void {
    this.message.info('Funcionalidade de importar será implementada em versão futura');
  }
}
